/**
 * Developer Dashboard
 * Scoped admin panel for developers to manage their projects
 * 
 * Features:
 * - View and manage assigned projects
 * - Verify buyers for their projects (instant verification)
 * - Post updates for their projects
 * - Handle pre/post possession issues (Phase 3)
 */

import { useState, useEffect, useMemo } from 'react';
import { authMarker } from '@/lib/sessionClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Building2, Users, CheckCircle, XCircle, Clock, FileText, 
  Eye, Download, MessageSquare, TrendingUp, AlertCircle,
  Plus, Send, Image, Calendar, MapPin, Phone, Mail,
  ChevronRight, Search, Filter, RefreshCw, Shield, Info,
  Loader2, Home, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeveloperDashboard({ user, defaultTab = 'overview' }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const filteredVerifications = useMemo(
    () => pendingVerifications.filter(v => selectedProject === 'all' || v.project_id === selectedProject),
    [pendingVerifications, selectedProject]
  );

  useEffect(() => {
    fetchDeveloperData();
  }, []);

  const fetchDeveloperData = async () => {
    setLoading(true);
    try {
      // Fetch developer's projects and stats
      const token = authMarker();
      const headers = { Authorization: `Bearer ${token}` };
      
      const [projectsRes, statsRes, verificationsRes] = await Promise.all([
        axios.get(`${API}/api/developer/my-projects`, { headers }),
        axios.get(`${API}/api/developer/stats`, { headers }),
        axios.get(`${API}/api/developer/pending-verifications`, { headers })
      ]);
      
      setProjects(projectsRes.data.projects || []);
      setStats(statsRes.data);
      setPendingVerifications(verificationsRes.data.verifications || []);
    } catch (err) {
      console.error('Error fetching developer data:', err);
      // Set empty/default data for demo
      setProjects([]);
      setStats({
        total_projects: 0,
        total_verified_buyers: 0,
        pending_verifications: 0,
        recent_updates: 0
      });
      setPendingVerifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyBuyer = async (verificationId, action, remarks = '') => {
    try {
      await axios.post(`${API}/api/developer/verify-buyer`, {
        verification_id: verificationId,
        action: action, // 'approve' or 'reject'
        remarks: remarks
      }, {
        headers: { Authorization: `Bearer ${authMarker()}` }
      });
      
      toast.success(action === 'approve' ? 'Buyer verified successfully' : 'Verification rejected');
      fetchDeveloperData(); // Refresh data
    } catch (err) {
      toast.error('Failed to process verification');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="developer-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Building2 className="h-8 w-8 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Developer Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your projects, verify buyers, and post updates
            </p>
          </div>
        </div>
        <Button onClick={fetchDeveloperData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Building2 className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.total_projects || 0}</div>
                <div className="text-xs text-muted-foreground">My Projects</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.total_verified_buyers || 0}</div>
                <div className="text-xs text-muted-foreground">Verified Buyers</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={stats?.pending_verifications > 0 ? 'border-amber-300' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.pending_verifications || 0}</div>
                <div className="text-xs text-muted-foreground">Pending Verifications</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.recent_updates || 0}</div>
                <div className="text-xs text-muted-foreground">Updates Posted</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="projects">My Projects</TabsTrigger>
          <TabsTrigger value="verifications" className="relative">
            Verifications
            {stats?.pending_verifications > 0 && (
              <Badge className="ml-1 h-5 w-5 p-0 justify-center bg-amber-500">
                {stats.pending_verifications}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="updates">Post Updates</TabsTrigger>
          <TabsTrigger value="issues">Buyer Issues</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {projects.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No Projects Assigned Yet</h3>
                <p className="text-muted-foreground mb-4">
                  You need to claim your projects and get them approved by FBC Admin.
                </p>
                <Button onClick={() => setActiveTab('projects')}>
                  <Plus className="h-4 w-4 mr-2" /> Claim Your Projects
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Quick Actions */}
              {stats?.pending_verifications > 0 && (
                <Alert className="border-amber-300 bg-amber-50">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="flex items-center justify-between">
                    <span className="text-amber-800">
                      You have <strong>{stats.pending_verifications}</strong> pending buyer verifications
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setActiveTab('verifications')}>
                      Review Now <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Project Summary */}
              <div className="grid md:grid-cols-2 gap-4">
                {projects.slice(0, 4).map(project => (
                  <Card key={project.id} className="hover:border-indigo-300 cursor-pointer transition-colors">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{project.name}</h4>
                          <p className="text-xs text-muted-foreground">{project.rera_number}</p>
                        </div>
                        <Badge variant="outline">{project.verified_buyers || 0} buyers</Badge>
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {project.city}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {project.pending_verifications || 0} pending
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* My Projects Tab */}
        <TabsContent value="projects" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Your Managed Projects</h3>
            <ClaimProjectDialog onClaim={fetchDeveloperData} />
          </div>

          {projects.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Info className="h-10 w-10 mx-auto text-blue-500 mb-3" />
                <h4 className="font-semibold mb-2">How to Claim Your Projects</h4>
                <ol className="text-sm text-muted-foreground text-left max-w-md mx-auto space-y-2">
                  <li>1. Click "Claim Project" and search for your RERA-registered project</li>
                  <li>2. Submit your claim with supporting documents</li>
                  <li>3. FBC Admin will verify and approve your claim</li>
                  <li>4. Once approved, you can manage buyers and post updates</li>
                </ol>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {projects.map(project => (
                <Card key={project.id}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Building2 className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{project.name}</h4>
                          <p className="text-sm text-muted-foreground">{project.rera_number}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span><MapPin className="h-3 w-3 inline mr-1" />{project.city}</span>
                            <span><Users className="h-3 w-3 inline mr-1" />{project.total_units || 0} units</span>
                            <span><CheckCircle className="h-3 w-3 inline mr-1" />{project.verified_buyers || 0} verified</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={project.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {project.status || 'active'}
                        </Badge>
                        {project.pending_verifications > 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            {project.pending_verifications} pending verifications
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Verifications Tab */}
        <TabsContent value="verifications" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Buyer Verification Requests</h3>
              <p className="text-sm text-muted-foreground">
                Review and verify buyers claiming ownership in your projects
              </p>
            </div>
            {projects.length > 1 && (
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {pendingVerifications.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <h3 className="font-semibold text-lg mb-2">All Caught Up!</h3>
                <p className="text-muted-foreground">
                  No pending buyer verifications at the moment.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredVerifications
                .map(verification => (
                  <VerificationCard 
                    key={verification.id} 
                    verification={verification}
                    onVerify={handleVerifyBuyer}
                  />
                ))
              }
            </div>
          )}
        </TabsContent>

        {/* Post Updates Tab */}
        <TabsContent value="updates" className="space-y-4">
          <PostUpdateForm projects={projects} onSuccess={fetchDeveloperData} />
        </TabsContent>

        {/* Buyer Issues Tab */}
        <TabsContent value="issues" className="space-y-4">
          <BuyerIssuesPanel projects={projects} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Verification Card Component
function VerificationCard({ verification, onVerify }) {
  const [viewingDocs, setViewingDocs] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleAction = async (action) => {
    setProcessing(true);
    await onVerify(verification.id, action, remarks);
    setProcessing(false);
  };

  return (
    <Card className="border-l-4 border-l-amber-400">
      <CardContent className="py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <Users className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-semibold">{verification.buyer_name}</h4>
                <Badge variant="outline" className="text-xs">
                  {verification.unit_number}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{verification.project_name}</p>
              <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                <span><Mail className="h-3 w-3 inline mr-1" />{verification.email}</span>
                <span><Phone className="h-3 w-3 inline mr-1" />{verification.phone}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Submitted: {new Date(verification.submitted_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Eye className="h-4 w-4 mr-1" /> View Docs
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Verification Documents</DialogTitle>
                  <DialogDescription>
                    Review the documents submitted by {verification.buyer_name}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {verification.documents?.map((doc, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span className="font-medium">{doc.type}</span>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <a href={doc.url} target="_blank" rel="noopener noreferrer">
                            <Download className="h-4 w-4 mr-1" /> Download
                          </a>
                        </Button>
                      </div>
                    </div>
                  )) || (
                    <p className="text-muted-foreground text-center py-4">
                      No documents uploaded yet
                    </p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Separator className="my-3" />

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs">Remarks (optional)</Label>
            <Input 
              placeholder="Add any notes about this verification..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="mt-1"
            />
          </div>
          <Button 
            variant="outline" 
            className="border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => handleAction('reject')}
            disabled={processing}
          >
            <XCircle className="h-4 w-4 mr-1" /> Reject
          </Button>
          <Button 
            className="bg-green-600 hover:bg-green-700"
            onClick={() => handleAction('approve')}
            disabled={processing}
          >
            <CheckCircle className="h-4 w-4 mr-1" /> Verify Buyer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Claim Project Dialog
function ClaimProjectDialog({ onClaim }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await axios.get(`${API}/api/projects`, { params: { search: searchQuery } });
      setSearchResults(Array.isArray(res.data) ? res.data : (res.data?.projects || []));
    } catch (err) {
      toast.error('Search failed');
    }
  };

  const handleClaim = async () => {
    if (!selectedProject) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/developer/claim-project`, {
        project_id: selectedProject.id
      }, {
        headers: { Authorization: `Bearer ${authMarker()}` }
      });
      toast.success('Project claim submitted! FBC Admin will review and approve.');
      setOpen(false);
      setSelectedProject(null);
      setSearchQuery('');
      onClaim();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" /> Claim Project
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Claim Your Project</DialogTitle>
          <DialogDescription>
            Search for your RERA-registered project and submit a claim
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Search by project name or RERA number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>

          {searchResults.length > 0 && (
            <ScrollArea className="h-48 border rounded-lg p-2">
              {searchResults.map(project => (
                <div
                  key={project.id}
                  className={`p-2 rounded cursor-pointer transition-colors ${
                    selectedProject?.id === project.id 
                      ? 'bg-indigo-100 border-indigo-300' 
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="font-medium text-sm">{project.name}</div>
                  <div className="text-xs text-muted-foreground">{project.rera_number}</div>
                </div>
              ))}
            </ScrollArea>
          )}

          {selectedProject && (
            <Alert className="bg-indigo-50 border-indigo-200">
              <Building2 className="h-4 w-4 text-indigo-600" />
              <AlertDescription className="text-indigo-800">
                <strong>Selected:</strong> {selectedProject.name}
                <br />
                <span className="text-xs">{selectedProject.rera_number}</span>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleClaim} disabled={!selectedProject || submitting}>
            {submitting ? 'Submitting...' : 'Submit Claim'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Post Update Form
function PostUpdateForm({ projects, onSuccess }) {
  const [selectedProject, setSelectedProject] = useState('');
  const [updateType, setUpdateType] = useState('progress');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedProject || !title || !content) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/developer/post-update`, {
        project_id: selectedProject,
        update_type: updateType,
        title,
        content
      }, {
        headers: { Authorization: `Bearer ${authMarker()}` }
      });
      toast.success('Update posted successfully!');
      setTitle('');
      setContent('');
      onSuccess();
    } catch (err) {
      toast.error('Failed to post update');
    } finally {
      setSubmitting(false);
    }
  };

  if (projects.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-amber-500 mb-3" />
          <h4 className="font-semibold mb-2">No Projects to Update</h4>
          <p className="text-sm text-muted-foreground">
            You need to have approved projects before posting updates.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Post Project Update</CardTitle>
        <CardDescription>
          Share construction progress, announcements, or important information with buyers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Select Project *</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Choose a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Update Type *</Label>
            <Select value={updateType} onValueChange={setUpdateType}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="progress">Construction Progress</SelectItem>
                <SelectItem value="announcement">Announcement</SelectItem>
                <SelectItem value="timeline">Timeline Update</SelectItem>
                <SelectItem value="general">General Update</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Title *</Label>
          <Input
            className="mt-1"
            placeholder="Brief title for this update"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div>
          <Label>Content *</Label>
          <Textarea
            className="mt-1 min-h-32"
            placeholder="Describe the update in detail..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline">
            <Image className="h-4 w-4 mr-2" /> Add Photos
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            <Send className="h-4 w-4 mr-2" /> 
            {submitting ? 'Posting...' : 'Post Update'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// Buyer Issues Panel - Developer view of issues raised by buyers
function BuyerIssuesPanel({ projects }) {
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [filter, setFilter] = useState('all');
  const filteredIssues = useMemo(
    () => issues.filter(issue => filter === 'all' || issue.status === filter),
    [issues, filter]
  );
  const [selectedProject, setSelectedProject] = useState('all');

  useEffect(() => {
    fetchIssues();
  }, [selectedProject]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const projectIds = selectedProject === 'all' 
        ? projects.map(p => p.id).join(',')
        : selectedProject;
      
      const res = await axios.get(`${API}/api/developer/project-issues?project_ids=${projectIds}`, {
        headers: { Authorization: `Bearer ${authMarker()}` }
      });
      setIssues(res.data.issues || []);
    } catch (err) {
      console.error('Failed to fetch issues:', err);
      // Show placeholder data for now
      setIssues([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryInfo = (category) => {
    const categories = {
      pre_possession: { label: 'Pre-Possession', color: 'bg-blue-100 text-blue-800' },
      post_possession: { label: 'Post-Possession', color: 'bg-purple-100 text-purple-800' },
      quality: { label: 'Quality Issue', color: 'bg-red-100 text-red-800' },
      delay: { label: 'Delay', color: 'bg-amber-100 text-amber-800' },
      documentation: { label: 'Documentation', color: 'bg-green-100 text-green-800' },
      other: { label: 'Other', color: 'bg-gray-100 text-gray-800' }
    };
    return categories[category] || categories.other;
  };

  const getStatusInfo = (status) => {
    const statuses = {
      open: { label: 'Open', color: 'bg-red-100 text-red-800 border-red-300' },
      in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-800 border-amber-300' },
      resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800 border-green-300' },
      closed: { label: 'Closed', color: 'bg-gray-100 text-gray-800 border-gray-300' }
    };
    return statuses[status] || statuses.open;
  };

  if (projects.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold text-lg mb-2">No Projects Yet</h3>
          <p className="text-muted-foreground">
            Claim your projects first to see buyer-reported issues.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header & Filters */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-semibold">Buyer-Reported Issues</h3>
          <p className="text-sm text-muted-foreground">
            Monitor and respond to issues raised by verified buyers
          </p>
        </div>
        <div className="flex items-center gap-2">
          {projects.length > 1 && (
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id || p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchIssues}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Info Alert */}
      <Alert className="bg-indigo-50 border-indigo-200">
        <Info className="h-4 w-4 text-indigo-600" />
        <AlertDescription className="text-indigo-800">
          <strong>Trust Bridge:</strong> Responding to buyer issues promptly builds trust and can prevent escalations to RERA.
        </AlertDescription>
      </Alert>

      {/* Issues List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : issues.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h3 className="font-semibold text-lg mb-2">No Issues Reported</h3>
            <p className="text-muted-foreground">
              Great news! No buyer issues have been reported for your projects.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredIssues
            .map(issue => {
              const categoryInfo = getCategoryInfo(issue.category);
              const statusInfo = getStatusInfo(issue.status);
              
              return (
                <Card key={issue.id || issue.id} className="hover:border-indigo-300 transition-colors">
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold">{issue.title}</h4>
                          <Badge className={categoryInfo.color}>{categoryInfo.label}</Badge>
                          <Badge variant="outline" className={statusInfo.color}>{statusInfo.label}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {issue.description}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span><Building2 className="h-3 w-3 inline mr-1" />{issue.project_name}</span>
                          <span><Home className="h-3 w-3 inline mr-1" />{issue.unit_number || 'N/A'}</span>
                          <span><Users className="h-3 w-3 inline mr-1" />{issue.reporter_name}</span>
                          <span><Clock className="h-3 w-3 inline mr-1" />{new Date(issue.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        <MessageSquare className="h-4 w-4 mr-1" /> Respond
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </div>
      )}
    </div>
  );
}
